import { ListItemText, Stack, Typography } from "@mui/material";
import { parseIpv4 } from "@verity/proctoring-option";
import { IpAddressRange } from "@verity/proctoring-option/types";
import { useCallback, useMemo, useState } from "react";
import {
	CoreButton,
	CoreMenuButton,
	CoreMenuItem,
	CoreRadioCard,
	CoreRadioCardIcon,
	CoreRadioCardSelectIcon,
	CoreTextField,
} from "../../../core";
import { useFocusElementOnMount } from "../../../hooks";
import i18nPackage from "../../../i18n/i18n";
import IpAddressIcon from "../../images/configuration/ip-address.svg?react";
import styles from "./IpAddressList.module.css";
import {
	BaseListContainer,
	BaseListFooterContainer,
	BaseListHeaderContainer,
	BaseListInfoArea,
	CommonListProps,
} from "./OptionListsComponents";

type IpRangeCardProps = {
	range: IpAddressRange;
	onDelete: () => void;
};

function IpRangeCard({ range, onDelete }: IpRangeCardProps) {
	const [menuShowing, setMenuShowing] = useState<boolean>(false);

	return (
		<CoreRadioCard
			slotProps={{
				container: { className: styles.row },
				button: { className: styles.button },
			}}
			// onClick={onSelect}
			selected={true}
			secondaryAction={
				onDelete ? (
					<CoreMenuButton
						menuShowing={menuShowing}
						setMenuShowing={setMenuShowing}
						aria-label={i18nPackage.t("editQuizPage.geofenceList.actionAriaLabel")}
					>
						{onDelete && (
							<CoreMenuItem
								onClick={() => {
									setMenuShowing(false);
									onDelete();
								}}
							>
								<ListItemText primary={i18nPackage.t("editQuizPage.geofenceList.remove")} />
							</CoreMenuItem>
						)}
					</CoreMenuButton>
				) : null
			}
		>
			<Stack direction="row" alignItems="center" spacing="10px" width="100%">
				<CoreRadioCardIcon enabled={true} icon={<IpAddressIcon color="red" />} />
				<div>
					<Typography width="100%" color="#000" fontSize={14}>
						{range.min} - {range.max}
					</Typography>
				</div>
			</Stack>
			<CoreRadioCardSelectIcon selected={true} />
		</CoreRadioCard>
	);
}

type IpAddressListProps = {
	allowedIpAddresses: IpAddressRange[];
	onUpdate: (allowedIpAddresses: IpAddressRange[]) => void;
} & CommonListProps;

export default function IpAddressList({ allowedIpAddresses, onUpdate, focusOnMount, onFocus }: IpAddressListProps) {
	const { ref } = useFocusElementOnMount<HTMLDivElement>(focusOnMount, onFocus);
	const [minValue, setMinValue] = useState("");
	const [maxValue, setMaxValue] = useState("");

	const canAdd = useMemo(() => {
		const minNumber = parseIpv4(minValue);
		const maxNumber = parseIpv4(maxValue);
		return minNumber !== undefined && maxNumber !== undefined && minNumber <= maxNumber;
	}, [minValue, maxValue]);

	const handleAdd = useCallback(() => {
		if (!canAdd) return;
		onUpdate([...allowedIpAddresses, { min: minValue.trim(), max: maxValue.trim() }]);
		setMinValue("");
		setMaxValue("");
	}, [allowedIpAddresses, canAdd, maxValue, minValue, onUpdate]);

	const handleRemove = useCallback(
		(index: number) => {
			onUpdate(allowedIpAddresses.filter((_, i) => i !== index));
		},
		[allowedIpAddresses, onUpdate],
	);

	return (
		<BaseListContainer ref={ref}>
			<BaseListHeaderContainer>
				<Stack direction="row" justifyContent="space-between">
					<BaseListInfoArea
						icon={<IpAddressIcon />}
						title={i18nPackage.t("editQuizPage.ipAddressListTSX.title")}
						description={i18nPackage.t("editQuizPage.ipAddressListTSX.description")}
					/>
					<CoreButton onClick={handleAdd} disabled={!canAdd} className={styles.addRowButton}>
						{i18nPackage.t("editQuizPage.ipAddressListTSX.add")}
					</CoreButton>
				</Stack>
				<div className={styles.addRow}>
					<Stack>
						<Typography className={styles.fieldLabel}>
							{i18nPackage.t("editQuizPage.ipAddressListTSX.minLabel")}
						</Typography>
						<CoreTextField
							className={styles.addRowTextfield}
							ariaLabel={i18nPackage.t("editQuizPage.ipAddressListTSX.minLabel")}
							placeholder={"192.168.1.1"}
							value={minValue}
							onChange={(e) => setMinValue(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									handleAdd();
								}
							}}
						/>
					</Stack>
					<Stack>
						<Typography className={styles.fieldLabel}>
							{i18nPackage.t("editQuizPage.ipAddressListTSX.maxLabel")}
						</Typography>
						<CoreTextField
							className={styles.addRowTextfield}
							ariaLabel={i18nPackage.t("editQuizPage.ipAddressListTSX.maxLabel")}
							placeholder={"192.168.1.255"}
							value={maxValue}
							onChange={(e) => setMaxValue(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									handleAdd();
								}
							}}
						/>
					</Stack>
				</div>
			</BaseListHeaderContainer>
			<BaseListFooterContainer>
				{allowedIpAddresses.map((range, index) => {
					return <IpRangeCard key={index} range={range} onDelete={() => handleRemove(index)} />;
				})}
			</BaseListFooterContainer>
		</BaseListContainer>
	);
}
